import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]); // { id, type, message, title, duration, action, icon, onClick }

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(({ type = 'info', message = '', title = '', duration = 4000, action = null, icon = null, onClick = null } = {}) => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setToasts((prev) => [...prev, { id, type, message, title, duration, action, icon, onClick }]);
    return id;
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-[1000] space-y-2 w-[92vw] max-w-sm pointer-events-none">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }) {
  const { type, title, message, duration, action, icon: IconComponent, onClick } = toast;
  useEffect(() => {
    const id = setTimeout(onClose, duration || 4000);
    return () => clearTimeout(id);
  }, [duration, onClose]);

  const palette = type === 'success'
    ? { wrap: 'border-emerald-500/40 text-emerald-200', dot: 'bg-emerald-400' }
    : type === 'error'
    ? { wrap: 'border-red-500/40 text-red-200', dot: 'bg-red-400' }
    : type === 'webhook'
    ? { wrap: 'border-sky-500/50 text-sky-200 bg-sky-950/90 shadow-[0_0_24px_rgba(14,165,233,0.2)]', dot: 'bg-sky-400 animate-ping' }
    : { wrap: 'border-sky-500/40 text-sky-200', dot: 'bg-sky-400' };

  const handleContainerClick = () => {
    if (typeof onClick === 'function') {
      onClick();
      onClose();
    }
  };

  return (
    <div
      onClick={handleContainerClick}
      className={`pointer-events-auto border rounded-xl bg-[#0a1412]/95 backdrop-blur shadow-lg shadow-black/40 p-3 text-sm flex items-start gap-2.5 animate-[toastIn_0.15s_ease-out] ${palette.wrap} ${onClick ? 'cursor-pointer hover:border-sky-400/80 transition-colors' : ''}`}
    >
      {IconComponent ? (
        <div className="mt-0.5 shrink-0 text-sky-400">
          <IconComponent className="w-4 h-4" />
        </div>
      ) : (
        <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${palette.dot}`} />
      )}
      <div className="flex-1 min-w-0">
        {title && <div className="font-semibold truncate text-slate-100">{title}</div>}
        {message && <div className="text-sm break-words">{message}</div>}
        {action && (
          <div className="mt-1.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick?.();
                onClose();
              }}
              className="text-xs font-semibold px-2 py-0.5 rounded border border-sky-500/40 bg-sky-500/15 text-sky-200 hover:bg-sky-500/30 transition"
            >
              {action.label || 'View'}
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="text-xs text-slate-400 hover:text-slate-200 shrink-0"
      >
        Dismiss
      </button>
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
